import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { easeOutStrong } from '../../utils/motion';
type Edge = {
  d: string;
  stroke: string;
  dashed?: boolean;
  delay: number;
};
const knowledgeNodes = [{
  x: 330,
  y: 90,
  r: 9,
  label: 'Studies',
  lx: 0,
  ly: -16
}, {
  x: 424,
  y: 148,
  r: 7,
  label: 'Sources',
  lx: 0,
  ly: -14
}, {
  x: 352,
  y: 204,
  r: 13,
  label: 'Concepts',
  lx: 22,
  ly: 4,
  anchor: 'start'
}, {
  x: 432,
  y: 262,
  r: 8,
  label: 'Findings',
  lx: 0,
  ly: 22
}, {
  x: 318,
  y: 290,
  r: 6,
  label: '',
  lx: 0,
  ly: 0
}];
const edges: Edge[] = [{
  d: 'M330 90 L352 204',
  stroke: '#245B8F',
  delay: 0.1
}, {
  d: 'M424 148 L352 204',
  stroke: '#245B8F',
  delay: 0.15
}, {
  d: 'M330 90 L424 148',
  stroke: '#245B8F',
  delay: 0.2
}, {
  d: 'M352 204 L432 262',
  stroke: '#245B8F',
  delay: 0.25
}, {
  d: 'M352 204 L318 290',
  stroke: '#245B8F',
  delay: 0.3
}, {
  d: 'M432 262 L318 290',
  stroke: '#245B8F',
  delay: 0.35
}, {
  d: 'M188 170 C 240 170, 280 204, 339 204',
  stroke: '#6FAE8A',
  dashed: true,
  delay: 0.45
}, {
  d: 'M130 228 C 130 310, 180 368, 224 368',
  stroke: '#2E8C88',
  delay: 0.55
}, {
  d: 'M318 296 C 312 340, 290 368, 256 368',
  stroke: '#245B8F',
  delay: 0.6
}];
export function IntelligenceDiagram() {
  const reduce = useReducedMotion();
  return <svg viewBox="0 0 480 440" className="w-full h-auto max-w-lg mx-auto" fill="none" role="img" aria-label="Diagram connecting human capability and connected knowledge into institutional decision-making" data-id="element-1197" data-mp="4449ojceyo1q">
      {edges.map(edge => <motion.path key={edge.d} d={edge.d} stroke={edge.stroke} strokeWidth={1.5} strokeDasharray={edge.dashed ? '4 5' : undefined} initial={reduce ? false : {
      pathLength: 0,
      opacity: 0
    }} animate={{
      pathLength: 1,
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: edge.delay,
      ease: easeOutStrong
    }} data-id="element-1198" data-mp="11sxtmf1uo06v" />)}

      {/* Human capability cluster */}
      <circle cx="130" cy="170" r="58" stroke="#D9E2EC" strokeWidth="1" data-id="element-1199" data-mp="1j2rxpkmwsj9k" />
      <circle cx="130" cy="170" r="34" stroke="#2E8C88" strokeWidth="1.5" data-id="element-1200" data-mp="1j54h828bf1oq" />
      <circle cx="130" cy="170" r="10" fill="#2E8C88" data-id="element-1201" data-mp="1e4gonc4fucse" />
      <circle cx="130" cy="112" r="5" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-1202" data-mp="19cq98rqn8yu4" />
      <circle cx="80" cy="199" r="5" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-1203" data-mp="14eezsi49gqcf" />
      <circle cx="188" cy="170" r="5" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-1204" data-mp="10ck0tk9syp1t" />
      <text x="130" y="100" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-1205" data-mp="10ah3ef6bltn2">
        Reasoning
      </text>
      <text x="70" y="220" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-1206" data-mp="qdkrdzh7qpdr">
        Judgment
      </text>
      <text x="130" y="252" fontSize="12" fill="#1F2A37" fontFamily="Manrope" textAnchor="middle" fontWeight="700" data-id="element-1207" data-mp="lh44t9pnwj2s">
        Human capability
      </text>

      {/* Knowledge cluster */}
      {knowledgeNodes.map(node => <g key={`${node.x}-${node.y}`} data-id="element-1208" data-mp="2oab053d83vdl">
          <circle cx={node.x} cy={node.y} r={node.r} fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-1209" data-mp="2o99lyq0wusrn" />
          {node.label && <text x={node.x + node.lx} y={node.y + node.ly} fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor={node.anchor === 'start' ? 'start' : 'middle'} fontWeight="500" data-id="element-1210" data-mp="2s9tlu1cvcahh">
              {node.label}
            </text>}
        </g>)}
      <circle cx="352" cy="204" r="4" fill="#245B8F" data-id="element-1211" data-mp="29dtrijwf4uuq" />
      <text x="400" y="322" fontSize="12" fill="#1F2A37" fontFamily="Manrope" textAnchor="middle" fontWeight="700" data-id="element-1212" data-mp="2ecerci7rdcin">
        Connected knowledge
      </text>

      {/* Institutions */}
      <rect x="222" y="352" width="36" height="32" rx="6" fill="#123B5D" data-id="element-1213" data-mp="2j8vh1gulvyxu" />
      <rect x="232" y="362" width="16" height="12" rx="2" fill="#FFFFFF" opacity="0.9" data-id="element-1214" data-mp="3pr23n67wrr29" />
      <text x="240" y="408" fontSize="12" fill="#1F2A37" fontFamily="Manrope" textAnchor="middle" fontWeight="700" data-id="element-1215" data-mp="3uotstsh64z2r">
        Better decisions
      </text>
    </svg>;
}